import {Box, Typography} from '@mui/material';
import Cookies from 'js-cookie';
import React, {Fragment, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {makePatchRefundFinishRequest} from 'main/factories/usecases';
import {
	AbandonFlowModal,
	AlertActions,
	AlertModal,
	FailToCompleteActionModal,
	StepProgressBar,
} from 'presentation/components';
import {CustomStepper} from 'presentation/components/CustomStepper';
import {MainHeader} from 'presentation/components/MainHeader';
import {PageWrapperLayout} from 'presentation/components/PageWrapperLayout';
import {
	FeaturesEnum,
	HomeEnum,
	OperationStateTypesEnum,
	ProfileEnum,
	RefundEnum,
} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useGetBeneficiaryBankDataQuery} from 'presentation/pages/private/profile/queries/useGetBeneficiaryBankDataQuery';
import {useValidateRefundBankQuery} from 'presentation/pages/private/profile/queries/useValidateRefundBankQuery';
import {BankValidityEnum} from 'domain/usecases/private/refund/request/get-validate-bank';
import {translations} from 'presentation/translations-new/';
import {BeneficiaryData} from '../../components';
import {ConclusionModal} from '../../components/ConclusionModal';
import {RefundVariantEnum} from '../../constants/refundVariantEnum';
import {useBackButtonExitModal, useRefundRequest} from '../../hooks';
import {useResetRefundFlow} from '../../hooks/useResetRefundFlow';
import {Conference, ReceiptRegistration, SendDocuments, ServiceType} from './components';
import {useRefundQueryParamsSync} from './hooks/useRefundQueryParamsSync';
import {useAccordionExpandedStore} from './store/useAccordionExpandedStore';
import {useExitFlowModalStore} from './store/useExitFlowModalStore';
import {useExitFlowSidebarRouteStore} from './store/useExitFlowSidebarRouteStore';
import {useRequestDataStore} from './store/useRequestDataStore';
import {useStepStore} from './store/useStepStore';
import {useSubmitStatusStore} from './store/useSubmitStatusStore';
import {StyledStack} from './styles';
import {RequestContentProps, StepEnum, SubmitStatusEnum} from './types';
import {Therapies} from './components/Therapies';
import {useTherapies} from './components/Therapies/hooks/useTherapies';
import {ConferenceTherapies} from './components/ConferenceTherapies';
import type {RefundFlowNavigationState} from '../../types/refundFlowNavigationState';
import {RequestV2} from '../RequestV2';

const {requestRefundTitle, steps, stepsTitle, discardModal} =
	translations['pt-br'].privateFlow.refund;

const {
	description: errorDescription,
	headerTitle: errorHeaderTitle,
	title: errorTitle,
} = translations['pt-br'].privateFlow.refund.conferencePage.errorModal;

const {invalidBankDataModal} = translations['pt-br'].privateFlow.refund.holderDataPage.modals;

const {tryAgain, goToHome} = translations['pt-br'].commons;

const RequestContent = ({step, firstVisit}: RequestContentProps) => {
	const navigate = useNavigate();
	const {state} = useLocation();
	const refundTermsReturnTo = (state as RefundFlowNavigationState | null)?.refundTermsReturnTo;
	const {isTablet} = useMediaQueries();
	const {resetRefundFlow} = useResetRefundFlow();
	const scrollToTopRef = document.getElementById('content-container');
	const {requests, isTherapiesFlowEnabled} = useRefundRequest();
	const {isAddOrEditPageMode, openModalEditDataAndCleanProcedures} = useTherapies();
	const {isEnabled: isRefundBankAndAgencyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_BANK_AND_AGENCY,
	);
	const {data: bankData} = useGetBeneficiaryBankDataQuery();
	const {data: validateBankData} = useValidateRefundBankQuery({
		bankCode: bankData?.bank?.code ?? '',
		agencyCode: bankData?.agency ?? '',
		enabled: isRefundBankAndAgencyEnabled,
	});

	const hideMainHeaderCommon = isTherapiesFlowEnabled && isAddOrEditPageMode;

	const nextStep = useStepStore(state => state.nextStep);
	const previousStep = useStepStore(state => state.previousStep);
	const resetSteps = useStepStore(state => state.resetStep);
	const setStep = useStepStore(state => state.setStep);
	const exitFlowModal = useExitFlowModalStore(state => state.open);
	const setExitFlowModal = useExitFlowModalStore(state => state.setOpen);
	const resetExitFlowModal = useExitFlowModalStore(state => state.reset);
	const requestData = useRequestDataStore(state => state.data);
	const resetAccordion = useAccordionExpandedStore(state => state.resetAccordion);
	const setSubmitStatus = useSubmitStatusStore(state => state.setStatus);
	const exitFlowSidebarRouteState = useExitFlowSidebarRouteStore(state => state.route);
	const resetExitFlowSidebarRouteState = useExitFlowSidebarRouteStore(state => state.reset);
	const [bankAccountModalState, setBankAccountModalState] = useState(false);
	const [conclusionModalState, setConclusionModalState] = useState<boolean>(false);
	const [concludeRequestPreviewState, setConcludeRequestPreviewState] = useState(
		OperationStateTypesEnum.NONE,
	);

	useRefundQueryParamsSync({step, firstVisit});

	const titleByStep = {
		[StepEnum.BENEFICIARY_DATA]: stepsTitle.beneficiaryData,
		[StepEnum.SERVICE_TYPE]: stepsTitle.service,
		[StepEnum.RECEIPT_REGISTRATION]: stepsTitle.receiptRegistration,
		[StepEnum.SEND_DOCUMENTS]: stepsTitle.sendDocuments,
		[StepEnum.REVIEW]: stepsTitle.review,
	};

	const titleByStepWhenTherapies = {
		[StepEnum.BENEFICIARY_DATA]: stepsTitle.beneficiaryData,
		[StepEnum.SERVICE_TYPE]: stepsTitle.service,
		[StepEnum.RECEIPT_REGISTRATION]: stepsTitle.receiptRegistration,
		[StepEnum.THERAPIES]: isTablet ? stepsTitle.procedures : undefined,
		[StepEnum.SEND_DOCUMENTS]: stepsTitle.sendDocuments,
		[StepEnum.REVIEW]: stepsTitle.review,
	};

	const title = isTherapiesFlowEnabled ? titleByStepWhenTherapies[step] : titleByStep[step];

	const stepsLabelsBase = [
		steps.holderData,
		steps.service,
		steps.receiptRegistration,
		steps.sendDocuments,
		steps.review,
	];

	const stepsLabelsWhenTherapies = [
		steps.holderData,
		steps.service,
		steps.receiptRegistration,
		steps.procedures,
		steps.sendDocuments,
		steps.review,
	];

	const stepsLabels = isTherapiesFlowEnabled ? stepsLabelsWhenTherapies : stepsLabelsBase;

	const IS_LOADING = concludeRequestPreviewState === OperationStateTypesEnum.SUBMITTING;
	const IS_ERROR = concludeRequestPreviewState === OperationStateTypesEnum.ERROR;

	const shouldOpenAbandonFlowModal =
		step === StepEnum.RECEIPT_REGISTRATION ||
		step === StepEnum.SEND_DOCUMENTS ||
		step === StepEnum.REVIEW ||
		step === StepEnum.THERAPIES;

	const handleGoToNextStep = async () => {
		if (
			isRefundBankAndAgencyEnabled &&
			validateBankData?.isValid === BankValidityEnum.INVALID
		) {
			setBankAccountModalState(true);
			return;
		}

		if (step === StepEnum.REVIEW) {
			try {
				setConcludeRequestPreviewState(OperationStateTypesEnum.SUBMITTING);

				await makePatchRefundFinishRequest(requestData.protocolCode.toString()).patch();

				setConcludeRequestPreviewState(OperationStateTypesEnum.DONE);
				setConclusionModalState(true);
				Cookies.remove('uploadFilesTerm');

				return;
			} catch (error) {
				setConcludeRequestPreviewState(OperationStateTypesEnum.ERROR);
				return;
			}
		}
		nextStep();
	};

	const handleBack = async () => {
		if (step === StepEnum.BENEFICIARY_DATA) {
			return navigate(RefundEnum.TERMS, {
				state: refundTermsReturnTo ? {refundTermsReturnTo} : undefined,
			});
		}

		if (
			step >= StepEnum.SEND_DOCUMENTS ||
			(isTherapiesFlowEnabled && step >= StepEnum.THERAPIES)
		) {
			setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		}

		if (isTherapiesFlowEnabled && step === StepEnum.THERAPIES) {
			openModalEditDataAndCleanProcedures();
		}

		if (step === StepEnum.SEND_DOCUMENTS && !isTherapiesFlowEnabled) {
			return setStep(StepEnum.RECEIPT_REGISTRATION);
		}

		return previousStep();
	};

	const handleClose = () => {
		setExitFlowModal(true);
		Cookies.remove('uploadFilesTerm');
	};

	const handleAbandonFlowModalAction = () => {
		resetSteps();
		resetAccordion();
		resetExitFlowModal();
		if (exitFlowSidebarRouteState) {
			navigate(exitFlowSidebarRouteState);
			resetExitFlowSidebarRouteState();
			return;
		}
		if (refundTermsReturnTo) {
			navigate(refundTermsReturnTo);
			return;
		}
		navigate(RefundEnum.REFUND);
	};

	const handleGoToHome = () => {
		navigate(HomeEnum.HOME_PAGE);
	};

	const pagesByStepBase = {
		[StepEnum.BENEFICIARY_DATA]: <BeneficiaryData handleNextStep={handleGoToNextStep} />,
		[StepEnum.SERVICE_TYPE]: <ServiceType handleNextStep={handleGoToNextStep} />,
		[StepEnum.RECEIPT_REGISTRATION]: <ReceiptRegistration />,
		[StepEnum.SEND_DOCUMENTS]: <SendDocuments handleNextStep={handleGoToNextStep} />,
		[StepEnum.REVIEW]: (
			<Conference handleNextStep={handleGoToNextStep} isLoading={IS_LOADING} />
		),
	};

	const pagesByStepWhenTherapies = {
		[StepEnum.BENEFICIARY_DATA]: <BeneficiaryData handleNextStep={handleGoToNextStep} />,
		[StepEnum.SERVICE_TYPE]: <ServiceType handleNextStep={handleGoToNextStep} />,
		[StepEnum.RECEIPT_REGISTRATION]: <ReceiptRegistration />,
		[StepEnum.THERAPIES]: <Therapies handleNextStep={handleGoToNextStep} />,
		[StepEnum.SEND_DOCUMENTS]: <SendDocuments handleNextStep={handleGoToNextStep} />,
		[StepEnum.REVIEW]: (
			<ConferenceTherapies handleNextStep={handleGoToNextStep} isLoading={IS_LOADING} />
		),
	};

	const pagesByStep = isTherapiesFlowEnabled ? pagesByStepWhenTherapies : pagesByStepBase;

	useBackButtonExitModal({
		setExitModal: setExitFlowModal,
		shouldOpenModal: shouldOpenAbandonFlowModal,
	});

	useEffect(() => {
		resetRefundFlow();
		resetExitFlowModal();
	}, []);

	useEffect(() => {
		if (scrollToTopRef) {
			scrollToTopRef.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [step]);

	return (
		<Fragment>
			<PageWrapperLayout title={requestRefundTitle}>
				{!hideMainHeaderCommon && (
					<MainHeader
						title={requestRefundTitle}
						handleBack={handleBack}
						variant="subRoute"
						handleClose={handleClose}
						dataTagId={{
							closeButton: RefundTaggingEnum.CLICK_FECHAR_SOLICITACAO_REEMBOLSO,
							backButton: RefundTaggingEnum.CLICK_VOLTAR_STEP_SOLICITACAO_REEMBOLSO,
						}}>
						<StyledStack gap={isTablet ? 2 : 3}>
							<Box pb={1}>
								{!isTablet ? (
									<CustomStepper activeStep={step} stepLabels={stepsLabels} />
								) : (
									<Box px={3}>
										<StepProgressBar
											currentStep={step + 1}
											totalSteps={stepsLabels.length}
										/>
									</Box>
								)}
							</Box>
							{title ? (
								<Typography paddingLeft={1} variant="h6" mb={1}>
									{title}
								</Typography>
							) : null}
						</StyledStack>
					</MainHeader>
				)}
				{pagesByStep[step]}
			</PageWrapperLayout>

			<AlertModal
				open={bankAccountModalState}
				onClose={() => setBankAccountModalState(false)}
				headerTitle={invalidBankDataModal.headerTitle}
				variant="info"
				customIconName="info-filled"
				title={invalidBankDataModal.title}
				description={invalidBankDataModal.description}
				footerContent={
					<AlertActions
						primaryAction={() => {
							setBankAccountModalState(false);
							navigate(ProfileEnum.PROFILE);
						}}
						secondaryAction={() => setBankAccountModalState(false)}
						primaryText={invalidBankDataModal.primaryAction}
						secondaryText={invalidBankDataModal.secondaryAction}
						flexDirection="column-reverse"
					/>
				}
			/>

			<ConclusionModal
				modalDataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_CONCLUSAO_SOLICITACAO}
				btnDataTagId={RefundTaggingEnum.CLICK_VOLTAR_PARA_TELA_INICIAL_SOLICITACAO}
				variant={RefundVariantEnum.DEFAULT}
				protocol={requests.protocol}
				open={conclusionModalState}
				setOpen={setConclusionModalState}
			/>

			<AbandonFlowModal
				closeButtondataTagId={RefundTaggingEnum.CLICK_FECHAR_ICONE_MODAL_ABANDONO_REEMBOLSO}
				backButtondataTagId={RefundTaggingEnum.CLICK_VOLTAR_MODAL_ABANDONO_REEMBOLSO}
				modaldataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_ABANDONO_REEMBOLSO}
				confirmButtondataTagId={
					RefundTaggingEnum.CLICK_CONFIRMAR_BOTAO_MODAL_ABANDONO_REEMBOLSO
				}
				open={exitFlowModal}
				setOpen={setExitFlowModal}
				handleConfirm={handleAbandonFlowModalAction}
				title={discardModal.title}
				headerTitle={discardModal.headerTitle}
				description={discardModal.description}
			/>

			<FailToCompleteActionModal
				dataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_DE_ERRO_AO_ENVIAR}
				headerTitle={errorHeaderTitle}
				description={errorDescription}
				title={errorTitle}
				open={IS_ERROR}
				setOpen={() => setConcludeRequestPreviewState(OperationStateTypesEnum.NONE)}
				primaryActionText={tryAgain}
				secondaryActionText={goToHome}
				fallbackAction={handleGoToHome}
				handleTryAgain={handleGoToNextStep}
			/>
		</Fragment>
	);
};

const Request = () => {
	const {isEnabled: isNeurotechEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_REFUND_NEUROTECH);
	const step = useStepStore(state => state.step);
	const stepsVisited = useRef(new Set<number>());
	const [firstVisit, setFirstVisit] = useState<boolean>(true);

	useEffect(() => {
		const isFirstVisit = !stepsVisited.current.has(step);
		setFirstVisit(isFirstVisit);
		stepsVisited.current.add(step);
	}, [step]);

	if (isNeurotechEnabled) return <RequestV2 />;

	return <RequestContent step={step} firstVisit={firstVisit} />;
};

export {Request};
